import { get } from '../../../lib/storage'
import { isNewDashboardEnabled } from '../../../containers/App/fulfilmentPickingConfig'
import { sortPacklistStatus } from '../../../lib/commonlyused'
import { mapCountByDisplayStatus, mapOrderStatuses } from './orderStatus'

export const CONSOLIDATED_COUNTS_URL = '/picking-service/v3/detail/count'
export const ORDER_DETAIL_COUNT_URL = '/picking-service/v3/detail/order/count'
export const PACKLIST_DETAIL_COUNT_URL =
  '/picking-service/v3/detail/packlist/count'

/**
 * This endpoint serves both the Orders and the Packlist Breakdown sections from the
 * picking database. Until it is verified in production the dashboard keeps the legacy
 * order-service + v3 packlists path behind this flag.
 */
export const isConsolidatedCountsEnabled = (splits) => {
  if (splits) {
    return isNewDashboardEnabled(splits)
  }
  try {
    const flags = JSON.parse(get('SPLIT_FEATURE_FLAGS'))
    return isNewDashboardEnabled(flags)
  } catch (error) {
    // A malformed flag blob must not break the dashboard - fall back to the legacy path.
    console.error('Error parsing SPLIT_FEATURE_FLAGS:', error)
    return false
  }
}

const buildStoreDateParams = ({ storeId, preferredDate, sapStoreId } = {}) => {
  const params = { preferredDate, storeId }
  if (sapStoreId) {
    params.sapStoreId = sapStoreId
  }
  return params
}

export const buildOrderCountParams = ({
  storeId,
  preferredDate,
  sapStoreId,
  excludePoa,
} = {}) => {
  const params = buildStoreDateParams({ storeId, preferredDate, sapStoreId })
  // Only sent when set, so including amended orders asks for exactly what it did before.
  if (excludePoa) {
    params.excludePoa = true
  }
  return params
}

export const buildPacklistCountParams = ({
  storeId,
  preferredDate,
  storeZone,
  sapStoreId,
} = {}) => {
  const params = buildStoreDateParams({ storeId, preferredDate, sapStoreId })
  // The zone filter keeps v3 packlists' `tag` key; 'none' means all zones.
  if (storeZone && storeZone !== 'none') {
    params.tag = storeZone
  }
  return params
}

// Backward-compatible alias for the consolidated endpoint.
export const buildConsolidatedParams = buildPacklistCountParams

/**
 * Maps a count array from the response into the shape CountTable consumes:
 * { slot: { startTime, endTime }, count: { STATUS: n } }
 *
 * The endpoint reports no slot type, so there is nothing to split ASAP and standard
 * slots on - every slot lands in one flat list.
 */
export const adaptCountEntries = (entries = []) => {
  const statuses = []
  let total = 0

  const slots = (entries || []).map((entry) => {
    const count = {}
    const countByStatus = entry?.countByStatus || []
    countByStatus.forEach(({ status, count: value }) => {
      if (statuses.indexOf(status) === -1) {
        statuses.push(status)
      }
      const parsed = parseInt(value, 10) || 0
      count[status] = parsed
      total += parsed
    })

    return {
      count,
      slot: {
        startTime: entry?.slot?.slotStartTime,
        endTime: entry?.slot?.slotEndTime,
      },
    }
  })

  return { slots, statuses, total }
}

const byStartTime = (a, b) => (a.slot.startTime < b.slot.startTime ? -1 : 1)

const sumSlotCounts = (slots) =>
  slots.reduce((count, { count: slotCount }) => {
    Object.keys(slotCount || {}).forEach((status) => {
      count[status] = (count[status] || 0) + slotCount[status]
    })
    return count
  }, {})

export const adaptOrderCountResponse = (
  response,
  { areOrdersScheduled } = {}
) => {
  const orderEntries = adaptCountEntries(response?.orderCount)

  const orderSlots = orderEntries.slots
    .map((entry) => ({
      ...entry,
      count: mapCountByDisplayStatus(entry.count),
    }))
    .sort(byStartTime)

  return {
    orderSlots: areOrdersScheduled ? orderSlots : null,
    noSlots: areOrdersScheduled ? null : { count: sumSlotCounts(orderSlots) },
    statuses: mapOrderStatuses(orderEntries.statuses),
    totalOrders: orderEntries.total,
  }
}

export const adaptPacklistCountResponse = (response) => {
  const packlistEntries = adaptCountEntries(response?.packlistCount)

  return {
    packlistSlots: packlistEntries.slots,
    packlistStatuses: sortPacklistStatus(packlistEntries.statuses),
    totalPacklists: packlistEntries.total,
  }
}

/**
 * Splits the single consolidated response into the props each section needs.
 * Missing or empty arrays yield zeroed sections rather than throwing, so a store/date
 * with no orders or packlists still renders.
 */
export const adaptConsolidatedResponse = (
  response,
  { areOrdersScheduled } = {}
) => ({
  orders: adaptOrderCountResponse(response, { areOrdersScheduled }),
  packlists: adaptPacklistCountResponse(response),
})
